import { Component, createRef } from 'preact';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { placePanel } from './DeckShowcasePicker';
import { UI } from '../utils/arena/uiSounds';
import { isPhoneLayout } from '../utils/platform';
import { t } from '../localization/t';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  BEVELED_BTN, INPUT_STYLE, DIALOG_STYLE, FourCorners,
} from '../lib/medievalTheme';

const PHONE = isPhoneLayout();

const FIELD_STYLE = { ...INPUT_STYLE, color: TEXT_BODY, backgroundColor: '#0e0a06' };

// Strong ease-out; the built-in CSS easings are too weak for a
// popover to feel intentional.
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

export const TAG_GLYPH = (
  <>
    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
    <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
  </>
);

/**
 * Assigned-tag chips plus a search input with a suggestion dropdown.
 * Existing tags (minus the assigned ones) are offered as rows; a query
 * that matches nothing offers a "Create" row - this and the tag menu
 * are the only places new tags come into existence.
 *
 * Controlled: `tags` is the assigned list, `allTags` the pool of
 * existing tag names; `onAdd` / `onRemove` mutate in the parent.
 */
export class TagEditor extends Component {
  state = { query: '', open: false };

  add(tag) {
    this.props.onAdd?.(tag);
    this.setState({ query: '' });
  }

  handleKeyDown = (e, suggestions, canCreate) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) this.add(suggestions[0]);
      else if (canCreate) this.add(this.state.query.trim());
    } else if (e.key === 'Backspace' && !this.state.query && (this.props.tags || []).length > 0) {
      this.props.onRemove?.(this.props.tags[this.props.tags.length - 1]);
    }
  };

  render() {
    const { tags = [], allTags = [] } = this.props;
    const { query, open } = this.state;
    const q = query.trim();
    const ql = q.toLowerCase();
    const assigned = new Set(tags.map((t) => t.toLowerCase()));
    const suggestions = allTags.filter((t) => !assigned.has(t.toLowerCase()) && (!ql || t.toLowerCase().includes(ql)));
    const canCreate = !!q && !assigned.has(ql) && !allTags.some((t) => t.toLowerCase() === ql);

    return (
      <div className="relative">
        <div className="flex items-center flex-wrap gap-1.5 px-2 py-1 rounded-lg" style={this.props.fieldStyle || FIELD_STYLE}>
          {tags.map((tag) => (
            <span
              key={tag}
              className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs"
              style={{ background: `${GOLD} 0.12)`, border: `1px solid ${GOLD} 0.3)`, color: TEXT_BODY }}
            >
              {tag}
              <button
                type="button"
                aria-label={t('Remove tag {tag}', { tag })}
                className="flex items-center justify-center rounded-full"
                style={{ width: '0.9cqw', height: '0.9cqw', color: TEXT_MUTED }}
                onMouseDown={(e) => { e.preventDefault(); this.props.onRemove?.(tag); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: '0.55cqw', height: '0.55cqw' }}>
                  <path d="m6 6 12 12" /><path d="m18 6-12 12" />
                </svg>
              </button>
            </span>
          ))}
          <input
            type="text"
            placeholder={tags.length === 0 ? (this.props.placeholder || t('Search or create tags…')) : ''}
            maxLength={60}
            value={query}
            onFocus={() => this.setState({ open: true })}
            onBlur={() => this.setState({ open: false })}
            onInput={(e) => this.setState({ query: e.target.value, open: true })}
            onKeyDown={(e) => this.handleKeyDown(e, suggestions, canCreate)}
            className="flex-1 min-w-[6cqw] px-1 py-1 text-sm outline-none"
            style={{ background: 'transparent', color: TEXT_BODY, border: 'none' }}
          />
        </div>
        {open && (suggestions.length > 0 || canCreate) ? (
          <div
            className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl overflow-hidden"
            style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.2)`, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}
          >
            {/* Rows highlight as inset rounded pills (one padding step
                in from the menu edge), matching the shared Select menu,
                instead of edge-to-edge bars. */}
            <div className="max-h-40 overflow-y-auto medieval-scrollbar p-1">
              {suggestions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left rounded-lg transition-colors"
                  style={{ color: TEXT_BODY }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  // mousedown, not click: it has to win against the
                  // input's blur closing the menu.
                  onMouseDown={(e) => { e.preventDefault(); this.add(tag); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{ width: '0.7cqw', height: '0.7cqw', color: TEXT_MUTED }}>
                    {TAG_GLYPH}
                  </svg>
                  <span className="truncate">{tag}</span>
                </button>
              ))}
              {canCreate ? (
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left rounded-lg transition-colors"
                  style={{ color: ACCENT_GOLD }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onMouseDown={(e) => { e.preventDefault(); this.add(q); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0" style={{ width: '0.7cqw', height: '0.7cqw' }}>
                    <path d="M12 5v14" /><path d="M5 12h14" />
                  </svg>
                  {t('Create "{q}"', { q })}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

/**
 * The per-deck tag menu: a dropdown anchored to the tile's tag button
 * (same canvas-local placement as DeckShowcasePicker, so it can't be
 * clipped at any canvas size). Search on top, then every tag as a
 * toggle row with the assigned ones checked and sorted first, plus a
 * "Create" row when the query matches nothing. Toggles apply
 * immediately; the menu stays open for tagging in one visit.
 */
export default class DeckTagMenu extends Component {
  state = { search: '', pos: null, origin: 'top left', entered: false };
  rootRef = createRef();

  constructor(props) {
    super(props);
    // Row order is frozen at open: assigned-first reads right when the
    // menu appears, but re-sorting live would shift rows under the
    // cursor the moment one is toggled. Tags created in this session
    // append at the end instead.
    const assigned = new Set((props.deck?.tags || []).map((t) => t.toLowerCase()));
    this._baseOrder = [
      ...(props.deck?.tags || []),
      ...(props.allTags || []).filter((t) => !assigned.has(t.toLowerCase())),
    ];
  }

  componentDidMount() {
    this._escId = pushDismiss(() => this.props.onClose?.());
    document.addEventListener('mousedown', this.handleOutsideDown, true);
    this.reposition();
  }

  componentDidUpdate() {
    this.reposition();
  }

  componentWillUnmount() {
    removeDismiss(this._escId);
    document.removeEventListener('mousedown', this.handleOutsideDown, true);
    if (this._enterRaf) cancelAnimationFrame(this._enterRaf);
  }

  // Clicks on any tile's tag button are left alone so the gallery can
  // toggle this menu shut (same button) or swap it to another deck.
  handleOutsideDown = (e) => {
    const target = e.target;
    if (this.rootRef.current?.contains(target)) return;
    if (target?.closest?.('.deck-tag-btn')) return;
    this.props.onClose?.();
  };

  // Measure, place against the canvas, and derive the transform origin
  // from which side of the anchor the panel landed on - a popover
  // should grow out of its trigger, not out of its own center.
  reposition() {
    const el = this.rootRef.current;
    if (!el || !this.props.anchor) return;
    const rect = el.getBoundingClientRect();
    const next = placePanel(this.props.anchor, rect.width, rect.height);
    if (!next) return;
    const canvasEl = document.querySelector('.app-canvas');
    const canvasTop = canvasEl ? canvasEl.getBoundingClientRect().top : 0;
    const canvasLeft = canvasEl ? canvasEl.getBoundingClientRect().left : 0;
    const openedBelow = next.top >= (this.props.anchor.bottom - canvasTop) - 1;
    const originX = Math.max(0, (this.props.anchor.left + this.props.anchor.width / 2 - canvasLeft) - next.left);
    const origin = `${originX}px ${openedBelow ? '0%' : '100%'}`;
    const { pos } = this.state;
    if (pos && Math.abs(pos.left - next.left) < 0.5 && Math.abs(pos.top - next.top) < 0.5 && origin === this.state.origin) return;
    this.setState({ pos: next, origin }, () => {
      if (this.state.entered) return;
      this._enterRaf = requestAnimationFrame(() => this.setState({ entered: true }));
    });
  }

  toggle(tag) {
    const assigned = this.props.deck?.tags || [];
    const has = assigned.includes(tag);
    this.props.onChange?.(has ? assigned.filter((t) => t !== tag) : [...assigned, tag]);
  }

  create(tag) {
    const assigned = this.props.deck?.tags || [];
    this.props.onChange?.([...assigned, tag]);
    this.setState({ search: '' });
  }

  render() {
    const { deck, allTags = [] } = this.props;
    const { search, pos, origin, entered } = this.state;
    const assigned = deck?.tags || [];
    const assignedSet = new Set(assigned.map((t) => t.toLowerCase()));
    const q = search.trim();
    const ql = q.toLowerCase();
    const baseSet = new Set(this._baseOrder.map((t) => t.toLowerCase()));
    const rows = [
      ...this._baseOrder,
      // Tags created while the menu is open (assigned but unknown at
      // open time) trail the frozen order.
      ...assigned.filter((t) => !baseSet.has(t.toLowerCase())),
    ].filter((t) => !ql || t.toLowerCase().includes(ql));
    const canCreate = !!q && !rows.some((t) => t.toLowerCase() === ql);

    return (
      <div
        ref={this.rootRef}
        className="fixed z-[60] flex flex-col"
        style={{
          left: pos ? pos.left : 0,
          top: pos ? pos.top : 0,
          // Hidden for the measuring pass only. One frame, no flash.
          visibility: pos ? 'visible' : 'hidden',
          width: PHONE ? '15rem' : '13cqw',
          maxHeight: PHONE ? '18rem' : '18cqw',
          background: '#0e0b08',
          border: `1px solid ${GOLD} 0.28)`,
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.75)',
          transformOrigin: origin,
          transform: entered ? 'scale(1)' : 'scale(0.95)',
          opacity: entered ? 1 : 0,
          transition: `transform 160ms ${EASE_OUT}, opacity 160ms ${EASE_OUT}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 shrink-0">
          <input
            type="text"
            autoFocus
            placeholder={t('Search or create tags...')}
            maxLength={60}
            value={search}
            className="w-full min-w-0 px-2 py-1 outline-none"
            style={{ ...INPUT_STYLE, borderRadius: 'var(--radius-sm)', color: TEXT_PRIMARY, fontSize: PHONE ? '0.875rem' : '0.6cqw' }}
            onInput={(e) => this.setState({ search: e.target.value })}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (rows.length > 0) this.toggle(rows[0]);
              else if (canCreate) this.create(q);
            }}
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto medieval-scrollbar px-1 pb-1">
          {rows.map((tag) => {
            const isAssigned = assignedSet.has(tag.toLowerCase());
            return (
              <button
                key={tag}
                type="button"
                className="w-full flex items-center gap-2 px-2 py-1 text-left rounded-lg transition-colors"
                style={{ color: isAssigned ? TEXT_PRIMARY : TEXT_BODY, fontSize: PHONE ? '0.8125rem' : '0.6cqw', ...(PHONE ? { paddingTop: '0.5rem', paddingBottom: '0.5rem' } : {}) }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.1)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onClick={() => this.toggle(tag)}
              >
                <span
                  className="flex items-center justify-center rounded shrink-0"
                  style={{
                    width: PHONE ? '1rem' : '0.8cqw',
                    height: PHONE ? '1rem' : '0.8cqw',
                    background: isAssigned ? `${GOLD} 0.18)` : 'transparent',
                    border: `1px solid ${GOLD} ${isAssigned ? '0.6)' : '0.25)'}`,
                    transition: 'background-color 120ms ease, border-color 120ms ease',
                  }}
                >
                  {isAssigned ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: PHONE ? '0.7rem' : '0.55cqw', height: PHONE ? '0.7rem' : '0.55cqw', color: ACCENT_GOLD }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                </span>
                <span className="truncate">{tag}</span>
              </button>
            );
          })}
          {canCreate ? (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-2 py-1 text-left rounded-lg transition-colors"
              style={{ color: ACCENT_GOLD, fontSize: PHONE ? '0.8125rem' : '0.6cqw' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.1)`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              onClick={() => this.create(q)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0" style={{ width: PHONE ? '0.8rem' : '0.6cqw', height: PHONE ? '0.8rem' : '0.6cqw' }}>
                <path d="M12 5v14" /><path d="M5 12h14" />
              </svg>
              {t('Create "{q}"', { q })}
            </button>
          ) : null}
          {rows.length === 0 && !canCreate ? (
            <div className="px-2 py-3 text-center" style={{ color: TEXT_MUTED, fontSize: PHONE ? '0.75rem' : '0.55cqw' }}>
              {allTags.length === 0 ? t('Type to create your first tag') : t('No tags match')}
            </div>
          ) : null}
        </div>

        <div
          className="shrink-0 px-2 py-1 text-center arena-heading"
          style={{ color: ACCENT_GOLD, fontSize: PHONE ? '0.6875rem' : '0.5cqw', borderTop: `1px solid ${GOLD} 0.18)` }}
        >
          {t('Tag this deck')}
        </div>
      </div>
    );
  }
}

/**
 * Tag housekeeping: lists every tag with its deck count and offers
 * delete (which strips the tag from all decks). Deliberately no
 * create here - tags come into existence where they're assigned.
 */
export class ManageTagsModal extends Component {
  state = { confirmTag: null };

  render() {
    const { tags = [], onClose, onDeleteTag } = this.props;
    const { confirmTag } = this.state;
    return (
      <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
        <div className="relative w-[24cqw]" style={{ ...DIALOG_STYLE, isolation: 'isolate' }}>
          <FourCorners />
          <div className="px-5 pt-5 pb-3">
            <h2 className="text-lg font-semibold arena-heading" style={{ color: TEXT_PRIMARY }}>{t('Manage Tags')}</h2>
            <p className="text-xs mt-1" style={{ color: TEXT_MUTED }}>{t('Deleting a tag removes it from every deck. New tags are created where you assign them.')}</p>
          </div>
          <div className="px-5 pb-5">
            <div className="max-h-[18cqw] overflow-y-auto medieval-scrollbar rounded-lg" style={{ border: `1px solid ${GOLD} 0.15)` }}>
              {tags.length === 0 ? (
                <div className="px-3 py-3 text-xs italic" style={{ color: TEXT_MUTED }}>{t('No tags yet.')}</div>
              ) : tags.map((tag) => (
                <div key={tag.name} className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: `1px solid ${GOLD} 0.08)` }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{ width: '0.75cqw', height: '0.75cqw', color: TEXT_MUTED }}>
                    {TAG_GLYPH}
                  </svg>
                  <span className="flex-1 min-w-0 truncate text-xs" style={{ color: TEXT_BODY }}>{tag.name}</span>
                  {confirmTag === tag.name ? (
                    <>
                      <button
                        type="button"
                        className="px-2 py-1 text-xs font-semibold rounded"
                        style={{ background: 'rgba(180,60,60,0.2)', color: '#e89090', border: '1px solid rgba(180,60,60,0.4)' }}
                        data-sound={UI.CANCEL}
                        onClick={() => { this.setState({ confirmTag: null }); onDeleteTag?.(tag.name); }}
                      >
                        {tag.count === 1 ? t('Remove from 1 deck') : t('Remove from {n} decks', { n: tag.count })}
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 text-xs rounded"
                        style={{ color: TEXT_MUTED }}
                        onClick={() => this.setState({ confirmTag: null })}
                      >
                        {t('Keep')}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="shrink-0 text-xs" style={{ color: TEXT_MUTED }}>{tag.count === 1 ? t('1 deck') : t('{n} decks', { n: tag.count })}</span>
                      <button
                        type="button"
                        aria-label={t('Delete tag {name}', { name: tag.name })}
                        className="shrink-0 flex items-center justify-center rounded transition-colors"
                        style={{ width: '1.25cqw', height: '1.25cqw', color: TEXT_MUTED }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#e89090'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MUTED; }}
                        onClick={() => this.setState({ confirmTag: tag.name })}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.75cqw', height: '0.75cqw' }}>
                          <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                className="px-4 py-2 text-xs font-medium transition-all"
                style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                data-sound={UI.CANCEL}
                onClick={onClose}
              >
                {t('Close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
